// Drives the code-verified change modals: the Account tab's sign-in
// email and phone, and the Profile tab's contact email. Two-step flow:
// Start posts (new_value, password) to /start; on success the modal
// advances to step 2 (code entry); Verify posts (new_value, code) to
// /verify; on success the modal closes and the page reloads so the tab
// re-reads from postgres.
//
// Inline FieldError surfacing: the server returns 422 with
// {field, error}; we render the message into the [data-field-error]
// span adjacent to the matching input.

import { onceGuard } from "./bind_once.js";

const firstBind = onceGuard();

const ROOT_SELECTOR = "[data-account-change-modal]";

export function initAccountChangeModal(root = document) {
  root.querySelectorAll(ROOT_SELECTOR).forEach(wireModal);
}

function wireModal(modal) {
  if (!firstBind(modal)) return;

  const startURL = modal.dataset.startUrl;
  const verifyURL = modal.dataset.verifyUrl;
  const valueField = modal.dataset.valueField; // "new_email" | "new_phone"
  if (!startURL || !verifyURL || !valueField) return;

  const stepStart = modal.querySelector("[data-modal-step='start']");
  const stepVerify = modal.querySelector("[data-modal-step='verify']");
  const startForm = stepStart?.querySelector("form");
  const verifyForm = stepVerify?.querySelector("form");
  if (!startForm || !verifyForm) return;

  let pendingValue = "";
  // Bumped on every close, so a start answer that lands after the dialog
  // closed changes nothing.
  let generation = 0;
  // The "Also use as my contact" checkbox lives on the start step
  // but the actual server-side decision happens at verify time. We
  // capture it here and forward into the verify form's hidden
  // sync_contact input so the user's choice survives the step
  // transition.
  let pendingSyncContact = "";

  startForm.addEventListener("submit", async (event) => {
    event.preventDefault();
    clearFieldErrors(stepStart);

    const data = new FormData(startForm);
    pendingSyncContact = data.get("sync_contact") ? "1" : "";
    const sent = generation;
    try {
      const resp = await postForm(startURL, data);
      if (sent !== generation) return;
      // A new code starts a clean step, not on the last code's refusal.
      resetStep(stepVerify);
      pendingValue = resp[valueField] || data.get(valueField) || "";
      const hidden = verifyForm.querySelector(`input[name='${valueField}']`);
      if (hidden) hidden.value = pendingValue;
      const syncHidden = verifyForm.querySelector("input[name='sync_contact']");
      if (syncHidden) syncHidden.value = pendingSyncContact;
      // Name the address the code went to, so a typo shows before the
      // person waits on a code that never arrives.
      stepVerify.querySelectorAll("[data-pending-value]").forEach((el) => {
        el.textContent = pendingValue;
      });
      switchStep(stepStart, stepVerify);
    } catch (err) {
      if (sent === generation) surfaceError(stepStart, err);
    }
  });

  verifyForm.addEventListener("submit", async (event) => {
    event.preventDefault();
    clearFieldErrors(stepVerify);

    const data = new FormData(verifyForm);
    if (!data.get(valueField)) data.set(valueField, pendingValue);
    if (!data.get("sync_contact") && pendingSyncContact) {
      data.set("sync_contact", pendingSyncContact);
    }

    try {
      await postForm(verifyURL, data);
      modal.classList.add("hidden");
      window.location.reload();
    } catch (err) {
      surfaceError(stepVerify, err);
    }
  });

  modal.querySelectorAll("[data-modal-back]").forEach((btn) => {
    btn.addEventListener("click", (event) => {
      event.preventDefault();
      clearFieldErrors(stepVerify);
      switchStep(stepVerify, stepStart);
    });
  });

  // The settings pages' dismissibleModalScript closes these dialogs on
  // Cancel, the backdrop, and Esc, and fires this. A reopened dialog
  // starts empty, with no typed password left in it.
  modal.addEventListener("modal-closed", () => {
    generation += 1;
    resetStep(stepStart);
    resetStep(stepVerify);
  });
}

async function postForm(url, formData) {
  const params = new URLSearchParams();
  for (const [k, v] of formData.entries()) {
    if (typeof v === "string") params.append(k, v);
  }

  const resp = await fetch(url, {
    method: "POST",
    body: params,
    headers: { Accept: "application/json" },
    credentials: "same-origin",
  });

  if (resp.ok) {
    try {
      return await resp.json();
    } catch (_) {
      return {};
    }
  }

  let body = {};
  try {
    body = await resp.json();
  } catch (_) {
    // not JSON — treat as a generic error
  }
  const err = new Error(`${resp.url} answered ${resp.status}`);
  if (typeof body.error === "string") err.userMessage = body.error;
  err.field = body.field || "";
  throw err;
}

function resetStep(step) {
  step.querySelector("form")?.reset();
  clearFieldErrors(step);
}

function clearFieldErrors(scope) {
  scope.querySelectorAll("[data-field-error]").forEach((el) => {
    el.textContent = "";
    el.classList.add("hidden");
  });
  scope.querySelectorAll("[aria-invalid]").forEach((el) => {
    el.removeAttribute("aria-invalid");
  });
}

function surfaceError(scope, err) {
  const fieldName = err.field || "";
  let target = null;
  if (fieldName) {
    target = scope.querySelector(`[data-field-error='${fieldName}']`);
    // Mark the box itself, so the error sits on the field that is wrong
    // and a screen reader announces it.
    const input = scope.querySelector(
      `input[name='${fieldName}']:not([type='hidden'])`,
    );
    if (input) input.setAttribute("aria-invalid", "true");
  }
  if (!target) {
    target = scope.querySelector("[data-field-error='_form']");
  }
  if (!target) return;
  if (!err.userMessage) console.error("account change failed", err);
  target.textContent =
    err.userMessage || "Something went wrong. Please try again.";
  target.classList.remove("hidden");
}

function switchStep(from, to) {
  if (from) from.classList.add("hidden");
  if (to) to.classList.remove("hidden");
}
